/**
 * Estado compartilhado da Sidebar (collapsed) entre Sidebar e Main:
 *  - persiste preferência do usuário em localStorage (nao muda sob o usuario
 *    a cada reload)
 *  - controlled-component: o caller (AppShell) segura o estado e passa
 *    para Sidebar (renderizacao) + para Main (computar padding-left)
 *
 * Bloco D (Sprint 2): força rail colapsado na faixa tablet (1024-1280px).
 * Nessa faixa não há ganho de UX em expandir — espaço horizontal é escasso.
 * Acima de 1280px a preferência do usuário (toggle) volta a valer.
 * Abaixo de 1024px a sidebar fixa some (drawer assume), então o valor de
 * `collapsed` é irrelevante para a sidebar fixa.
 */
import { useEffect, useState } from "react";
import { BP_TABLET, BP_DESKTOP_LARGE } from "../styles/canonical/breakpoints";

// Bugfix 2026-08-04: esta chave estava como "dasherp:sidebar:collapsed" e o
// `migrarPrefixosLegados()` do boot (src/index.jsx) renomeia todo `dasherp:*`
// para `pharuerp:*` a CADA carregamento. Resultado: a migracao movia o valor
// para um nome que este hook nao lia, o hook caia no default e regravava no
// nome antigo — e a sidebar recolhida nunca persistia, nem no mesmo
// dispositivo. Lendo o nome novo, o valor ja migrado finalmente e encontrado.
const COLLAPSED_STORAGE_KEY = "pharuerp:sidebar:collapsed";

// matchMedia query da faixa "rail forçado": [1024, 1280).
const FORCED_RAIL_QUERY = `(min-width: ${BP_TABLET}px) and (max-width: ${BP_DESKTOP_LARGE - 1}px)`;

function readUserCollapsed() {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(COLLAPSED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function readForcedRail() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia(FORCED_RAIL_QUERY).matches;
}

export function useSidebarPreference() {
  const [userCollapsed, setUserCollapsed] = useState(readUserCollapsed);
  const [forcedRail, setForcedRail] = useState(readForcedRail);

  // Persiste preferência do usuário.
  useEffect(() => {
    try {
      window.localStorage.setItem(COLLAPSED_STORAGE_KEY, String(userCollapsed));
    } catch {
      /* storage indisponivel — ignora */
    }
  }, [userCollapsed]);

  // Observa a faixa tablet (1024-1280) e força rail colapsado nela.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mql = window.matchMedia(FORCED_RAIL_QUERY);
    const update = () => setForcedRail(mql.matches);
    update();
    // addEventListener é o padrão moderno; alguns Safari antigos usam addListener.
    if (mql.addEventListener) {
      mql.addEventListener("change", update);
      return () => mql.removeEventListener("change", update);
    }
    mql.addListener(update);
    return () => mql.removeListener(update);
  }, []);

  // Estado EFETIVO: na faixa tablet, sempre colapsado; senão, preferência.
  const collapsed = forcedRail || userCollapsed;

  // Toggle só faz sentido fora da faixa forçada (>=1280). Na faixa tablet o
  // AppShell esconde/desabilita o botão de colapsar (forcedRail exposto).
  const toggle = () => setUserCollapsed((v) => !v);

  return { collapsed, setCollapsed: setUserCollapsed, toggle, forcedRail };
}
